import React, { useState, useCallback, useEffect, useRef } from "react";
import { getUserProfileByUsernameOrAddress } from "../../../api/profile/profile";
import { ButtonSpinner, errorMessageHandler, fetchUserProfileImage } from "../../../utils/utils";
import { useSelector, useDispatch } from "react-redux";
import MnemonicsConfirmationModal from "../../CreateNFT/SingleNFT/MnemonicsConfirmationModal";
import { setCypherSecret } from "../../../actions/auth";
import ContinueWithPassword from "../../CreateNFT/SingleNFT/ContinueWithPassword";
import CryptoJS from 'crypto-js';
import { transferNft } from "../../../api/nft/nft";
import MissingMnemonics from "../../Layouts/MissingMnemonics/MissingMnemonics";
import './TransferNft.css';

const TransferNft = ({ isOpen, onClose, id }) => {

    // 📦 Form state
    const [data, setData] = useState({
        recipient: "",
        note: ""
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [checkTransferLoading, setCheckTransferLoading] = useState(false);
    const [canTransfer, setCanTransfer] = useState(false);
    const [transferInfo, setTransferInfo] = useState(null);
    const [recipientError, setRecipientError] = useState("");
    const [passwordModal, setShowPasswordModal] = useState(false);
    const [mnemonicsConfirmationModal, setShowMnemonicsConfirmationModal] = useState(false);
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.Profile.profile);
    const cypherSecret = useSelector((state) => state.Auth.cypherSecret);

    // Debounce timer ref
    const debounceTimerRef = useRef(null);

    // ✅ Validation function
    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!data.recipient?.trim()) {
            newErrors.recipient = "Recipient username or address is required.";
        } else if (!canTransfer) {
            newErrors.recipient = "Please enter a valid recipient.";
        }
        else if (transferInfo && currentUser?.public_address && currentUser.public_address === transferInfo.publicAddress) {
            newErrors.recipient = "You cannot transfer this NFT to yourself.";
        }
        if (data.note && data.note.trim().length > 500) {
            newErrors.note = "Note must be less than 500 characters.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [data, canTransfer, transferInfo, currentUser]);

    // 🔍 Check recipient validity with API
    const checkRecipient = async (searchParam) => {
        if (!searchParam || searchParam.trim().length < 3) {
            setCanTransfer(false);
            setTransferInfo(null);
            setRecipientError("");
            return;
        }

        try {
            setCheckTransferLoading(true);
            setRecipientError("");
            const { data } = await getUserProfileByUsernameOrAddress({ searchParam });

            setCanTransfer(true);
            setTransferInfo({
                username: data.data.user_name,
                publicAddress: data.data.public_address,
                profileImage: data.data.profile_image,
                userId: data.data.user_id,
            });


        } catch (e) {
            setCanTransfer(false);
            setTransferInfo(null);
            setRecipientError("User not found. Please check the username or address.");
        } finally {
            setCheckTransferLoading(false);
        }
    };

    // 📝 Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;

        setData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }

        // Debounce recipient check (only call API after user stops typing for 800ms)
        if (name === "recipient") {
            // Clear previous timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Reset states immediately
            setCanTransfer(false);
            setTransferInfo(null);
            setRecipientError("");

            // Set new timer
            debounceTimerRef.current = setTimeout(() => {
                checkRecipient(value);
            }, 800); // Wait 800ms after user stops typing
        }
    };

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // 🚀 Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if user has NOT set up their wallet (public address)
        if (!currentUser.public_address) {
            return document.getElementById('confirmationMissingMnemonicsBtn').click();
        }

        // Validate form
        if (!validateForm()) {
            return;
        }

        // Show appropriate modal based on whether user has saved mnemonics
        if (cypherSecret) {
            setShowPasswordModal(true)
        }
        else {
            setShowMnemonicsConfirmationModal(true)
        }
    };

    const handleTransferNft = (mnemonics, publicAddress, saveMyMnemonics, mnemonicsPwd) => {
        if (!mnemonics) {

            setErrors(prev => ({
                ...prev,
                submit: "Mnemonics are required."
            }));
            return;
        }

        if (!publicAddress) {
            setErrors(prev => ({
                ...prev,
                submit: "Your profile address does not match the provided mnemonics. Please use the mnemonics you provided during sign-up.",
            }));

            return;
        }

        if (publicAddress.toLowerCase() !== currentUser?.public_address?.toLowerCase()) {
            setErrors(prev => ({
                ...prev,
                submit: "Your profile address does not match the provided mnemonics. Please use the mnemonics you provided during sign-up.",
            }));
            return;
        }
        // if (parseFloat(balances.NUC) <= 0) {
        //   setErrors(prev => ({
        //     ...prev,
        //     submit: "Balance should be greater than 0. Currently, you have 0 balance."
        //   }));
        // }

        // ✅ All good — process NFT minting
        processNft(mnemonics, saveMyMnemonics, mnemonicsPwd);
    };

    let processNft = async (mnemonics, saveMyMnemonics, mnemonicsPwd) => {
        if (saveMyMnemonics) {
            const cypher = CryptoJS.AES.encrypt(mnemonics, mnemonicsPwd).toString();
            dispatch(setCypherSecret(cypher));
        }

        try {
            setIsLoading(true)
            setErrors({})
            let { data } = await transferNft({ nftId: id, toUserId: transferInfo.userId, secret: mnemonics })

            // Pass success result with recipient info
            handleClose({
                success: true,
                recipientInfo: {
                    username: transferInfo.username,
                    publicAddress: transferInfo.publicAddress,
                    userId: transferInfo.userId,
                    hash: data.data.txHash
                }
            });
        } catch (error) {
            console.error("Transfer error:", error);
            setErrors(prev => ({
                ...prev,
                submit: errorMessageHandler(error)
            }));
        } finally {
            setIsLoading(false);
        }
    }


    // 🔒 Handle modal close
    const handleClose = (result = false) => {
        // Clear debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Reset all states
        setData({ recipient: "", note: "" });
        setErrors({});
        setIsLoading(false);
        setCheckTransferLoading(false);
        setCanTransfer(false);
        setTransferInfo(null);
        setRecipientError("");
        onClose(result);
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Modal Backdrop */}
            <div
                className="modal-backdrop fade show"
                onClick={handleClose}
                style={{ zIndex: 1040 }}
            ></div>

            {/* Modal */}
            <div
                className="modal fade show"
                id="transferModalDesign"
                tabIndex="-1"
                aria-labelledby="transferModalDesignLabel"
                aria-modal="true"
                role="dialog"
                style={{ display: 'block', zIndex: 1050 }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content transfer-nft-modal-content">
                        {/* Modern Header */}
                        <div className="modal-header border-0 pb-0">
                            <div className="w-100">
                                <div className="d-flex align-items-center mb-2">
                                    <div className="transfer-icon-wrapper me-3">
                                        <i className="fas fa-paper-plane"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h5 className="modal-title mb-1 fw-bold" id="transferModalDesignLabel">
                                            Transfer NFT
                                        </h5>
                                        <p className="text-muted small mb-0">
                                            Send this NFT to another wallet
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleClose}
                                aria-label="Close"
                                disabled={isLoading}
                            />
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body pt-3">
                                {/* 👤 Recipient Field */}
                                <div className="mb-4">
                                    <label className="form-label fw-semibold d-flex align-items-center mb-2">
                                        <i className="fas fa-user me-2 text-muted"></i>
                                        Recipient <span className="text-danger ms-1">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            name="recipient"
                                            className={`form-control shadow-none transfer-input ${errors.recipient ? 'is-invalid-custom' :
                                                    canTransfer ? 'is-valid-custom' : ''
                                                }`}
                                            placeholder="Enter username or wallet address..."
                                            value={data.recipient}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            autoFocus
                                        />
                                        {checkTransferLoading && (
                                            <div className="input-loading-spinner">
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Checking...</span>
                                                </div>
                                            </div>
                                        )}
                                        {canTransfer && !checkTransferLoading && (
                                            <div className="input-validation-icon success">
                                                <i className="fas fa-check-circle"></i>
                                            </div>
                                        )}
                                        {recipientError && !checkTransferLoading && (
                                            <div className="input-validation-icon error">
                                                <i className="fas fa-times-circle"></i>
                                            </div>
                                        )}
                                    </div>

                                    {errors.recipient && (
                                        <div className="error-feedback-custom mt-2">
                                            <i className="fas fa-exclamation-circle me-1"></i>
                                            {errors.recipient}
                                        </div>
                                    )}

                                    {recipientError && !errors.recipient && (
                                        <div className="recipient-error-card mt-3">
                                            <div className="d-flex align-items-start">
                                                <div className="error-icon-badge me-2">
                                                    <i className="fas fa-user-slash"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <strong className="d-block mb-1">User Not Found</strong>
                                                    <small>{recipientError}</small>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {canTransfer && transferInfo && (
                                        <div className="recipient-verified-card mt-3">
                                            <div className="d-flex align-items-start">
                                                <div className="recipient-avatar-wrapper me-3">
                                                    {transferInfo.profileImage ? (
                                                        <img
                                                            src={fetchUserProfileImage(transferInfo.profileImage)}
                                                            alt={transferInfo.username}
                                                            className="recipient-avatar"
                                                        />
                                                    ) : (
                                                        <div className="recipient-avatar-placeholder">
                                                            <i className="fas fa-user"></i>
                                                        </div>
                                                    )}
                                                    <div className="verified-badge">
                                                        <i className="fas fa-check"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <strong className="recipient-username">@{transferInfo.username}</strong>
                                                        <span className="verified-label ms-2">
                                                            <i className="fas fa-shield-check me-1"></i>
                                                            Verified
                                                        </span>
                                                    </div>
                                                    <div className="recipient-address">
                                                        <code className="small">{transferInfo.publicAddress}</code>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 📝 Note Field */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold d-flex align-items-center mb-2">
                                        <i className="fas fa-comment-dots me-2 text-muted"></i>
                                        Message (Optional)
                                    </label>
                                    <textarea
                                        name="note"
                                        className={`form-control shadow-none transfer-textarea ${errors.note ? 'is-invalid-custom' : ''
                                            }`}
                                        rows="3"
                                        placeholder="Add a personal message with your transfer..."
                                        value={data.note}
                                        onChange={handleChange}
                                        disabled={isLoading}
                                    />
                                    <div className="d-flex justify-content-between align-items-center mt-1">
                                        {errors.note ? (
                                            <small className="text-danger">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {errors.note}
                                            </small>
                                        ) : (
                                            <small className="text-muted">
                                                <i className="fas fa-info-circle me-1"></i>
                                                This message will be visible to the recipient
                                            </small>
                                        )}
                                        <small className={`character-count ${data.note.length > 450 ? 'text-warning' : 'text-muted'}`}>
                                            {data.note.length}/500
                                        </small>
                                    </div>
                                </div>

                                {/* ⚠️ Submit Error */}
                                {errors.submit && (
                                    <div className="submit-error-card">
                                        <div className="d-flex align-items-start">
                                            <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                                            <div>
                                                <strong className="d-block mb-1">Transfer Failed</strong>
                                                <span>{errors.submit}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-outline-dark px-4 py-2"
                                    onClick={handleClose}
                                    disabled={isLoading}
                                >
                                    <i className="fas fa-times me-2"></i>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn BtnBlack px-4 py-2"
                                    disabled={!canTransfer || isLoading || checkTransferLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Transferring...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane me-2"></i>
                                            Transfer NFT
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <MnemonicsConfirmationModal
                visible={mnemonicsConfirmationModal}
                currentUser={currentUser}
                onClose={() => setShowMnemonicsConfirmationModal(false)}
                onConfirm={(data) => {
                    handleTransferNft(data.mnemonics, data.publicAddress, data.saveMyMnemonics, data.mnemonicsPwd)
                    setShowMnemonicsConfirmationModal(false);
                }}
            ></MnemonicsConfirmationModal>

            <ContinueWithPassword
                visible={passwordModal}
                currentUser={currentUser}
                cypherSecret={cypherSecret}
                onClose={() => setShowPasswordModal(false)}
                onConfirm={(data) => {
                    if (data.forgotPassword) {
                        setShowPasswordModal(false)
                        setShowMnemonicsConfirmationModal(true);
                    }
                    else {
                        handleTransferNft(data.mnemonics, data.publicAddress)
                        setShowPasswordModal(false)
                    }
                }}
            />


            <MissingMnemonics callback={(e) => handleSubmit()} />

        </>
    );
};


export default TransferNft;



